import React, { memo, useCallback, useState, useMemo } from 'react'
import { HStack } from '../../../../../../ui/Stack'
import {
  CardActions,
  useCardActions,
  CardActionConfig,
} from '../../../../../../components/v2/card-actions'
import { useNavigate } from 'react-router-dom'
import { Modal } from 'antd'
import {
  UserDeleteOutlined,
  SwapOutlined,
  EditOutlined,
  CloseCircleOutlined,
  SaveOutlined,
  UserAddOutlined,
  WomanOutlined,
} from '@ant-design/icons'
import { DismissModal } from '../../modals/dismiss-modal'
import { TransferModal } from '../../modals/transfer-modal'
import { ChangePositionModal } from '../../modals/change-position-modal'
import { RehireModal } from '../../modals/rehire-modal'
import { EmployeeFormHeaderProps } from '../../../types/types'
import fetchAPI from '../../../../../../../lib/utils/fetch-api'
import { confirmDiscardChanges } from '../../../utils/confirm-discard-changes'

export const EmployeeFormHeader: React.FC<EmployeeFormHeaderProps> = memo(
  (props: EmployeeFormHeaderProps) => {
    const { id, employee, enums, isEditMode, hasUnsavedChanges, onRefresh, onEditModeChange, onSave } =
      props
    const navigate = useNavigate()

    const [isOpenDismissModal, setIsOpenDismissModal] = useState<boolean>(false)
    const [isOpenTransferModal, setIsOpenTransferModal] = useState<boolean>(false)
    const [isOpenChangePositionModal, setIsOpenChangePositionModal] = useState<boolean>(false)
    const [isOpenRehireModal, setIsOpenRehireModal] = useState<boolean>(false)

    const isNew = id === 'new'
    const status = employee?.status ?? (employee?.is_active !== false ? 'active' : 'dismissed')
    const isActive = status === 'active'
    const isDismissed = status === 'dismissed'

    const handleMaternityLeaveClick = useCallback(() => {
      Modal.confirm({
        title: 'Перевести в декрет',
        content: 'Перевести сотрудника в статус «В декрете»?',
        okText: 'Да',
        cancelText: 'Отмена',
        onOk: async () => {
          await fetchAPI(`/api/hr/employees/${employee?.id}/maternity-leave`, {
            method: 'POST',
          })
          if (onRefresh) onRefresh()
        },
      })
    }, [employee?.id, onRefresh])

    const attemptLeave = useCallback(
      async (action: () => void) => {
        if (!hasUnsavedChanges) {
          action()
          return
        }

        const confirmed = await confirmDiscardChanges()
        if (confirmed) {
          action()
        }
      },
      [hasUnsavedChanges],
    )

    const handleCancelClick = useCallback(() => {
      void attemptLeave(() => navigate('/hr-employees'))
    }, [attemptLeave, navigate])

    const handleDismissClick = useCallback(() => {
      setIsOpenDismissModal(true)
    }, [])

    const handleTransferClick = useCallback(() => {
      setIsOpenTransferModal(true)
    }, [])

    const handleChangePositionClick = useCallback(() => {
      setIsOpenChangePositionModal(true)
    }, [])

    const handleRehireClick = useCallback(() => {
      setIsOpenRehireModal(true)
    }, [])

    const handleEditClick = useCallback(() => {
      if (onEditModeChange) {
        onEditModeChange(true)
      }
    }, [onEditModeChange])

    const handleCancelEditClick = useCallback(() => {
      void attemptLeave(() => {
        if (onEditModeChange) {
          onEditModeChange(false)
        }
      })
    }, [attemptLeave, onEditModeChange])

    const handleDismissOk = useCallback(() => {
      setIsOpenDismissModal(false)
      if (onRefresh) {
        onRefresh()
      }
    }, [onRefresh])

    const handleTransferOk = useCallback(() => {
      setIsOpenTransferModal(false)
      if (onRefresh) {
        onRefresh()
      }
    }, [onRefresh])

    const handleChangePositionOk = useCallback(() => {
      setIsOpenChangePositionModal(false)
      if (onRefresh) {
        onRefresh()
      }
    }, [onRefresh])

    const handleRehireOk = useCallback(() => {
      setIsOpenRehireModal(false)
      if (onRefresh) {
        onRefresh()
      }
    }, [onRefresh])

    const actionsConfig = useMemo((): CardActionConfig[] => {
      const actions: CardActionConfig[] = []

      if (isEditMode) {
        // В режиме редактирования показываем кнопки "Сохранить" и "Отмена"
        actions.push({
          key: 'save',
          label: 'Сохранить',
          icon: <SaveOutlined />,
          onClick: async () => {
            if (onSave) {
              await onSave()
            }
          },
          condition: true,
          order: 5,
        })

        actions.push({
          key: 'cancel-edit',
          label: 'Отмена',
          icon: <CloseCircleOutlined />,
          onClick: handleCancelEditClick,
          danger: true,
          condition: true,
          order: 10,
        })
      } else {
        // В режиме просмотра показываем обычные кнопки
        if (!isNew) {
          actions.push({
            key: 'edit',
            label: 'Редактировать',
            icon: <EditOutlined />,
            onClick: handleEditClick,
            condition: true,
            order: 5,
          })
        }

        if (!isNew && isActive) {
          actions.push({
            key: 'maternity-leave',
            label: 'В декрет',
            icon: <WomanOutlined />,
            onClick: handleMaternityLeaveClick,
            condition: true,
            order: 38,
          })
        }

        if (!isNew && (isActive || status === 'maternity')) {
          actions.push({
            key: 'dismiss',
            label: 'Уволить',
            icon: <UserDeleteOutlined />,
            onClick: handleDismissClick,
            danger: true,
            condition: true,
            order: 40,
          })
        }

        if (!isNew && isActive) {
          actions.push({
            key: 'transfer',
            label: 'Перевести',
            icon: <SwapOutlined />,
            onClick: handleTransferClick,
            condition: true,
            order: 30,
          })

          actions.push({
            key: 'change-position',
            label: 'Изменить должность',
            icon: <EditOutlined />,
            onClick: handleChangePositionClick,
            condition: true,
            order: 20,
          })
        }

        if (!isNew && isDismissed) {
          actions.push({
            key: 'rehire',
            label: 'Принять повторно',
            icon: <UserAddOutlined />,
            onClick: handleRehireClick,
            condition: true,
            order: 35,
          })
        }

        actions.push({
          key: 'cancel',
          label: 'Отмена',
          icon: <CloseCircleOutlined />,
          onClick: handleCancelClick,
          danger: true,
          condition: isNew,
          order: 100,
        })
      }

      return actions
    }, [
      isNew,
      isActive,
      isDismissed,
      isEditMode,
      handleEditClick,
      handleCancelEditClick,
      handleDismissClick,
      handleMaternityLeaveClick,
      handleTransferClick,
      handleChangePositionClick,
      handleRehireClick,
      handleCancelClick,
      hasUnsavedChanges,
      onSave,
    ])

    const { primaryAction, secondaryActions } = useCardActions({ actions: actionsConfig })

    return (
      <>
        <HStack gap="8" className="mapWrapper" wrap justify="end">
          <CardActions primaryAction={primaryAction} secondaryActions={secondaryActions} />
        </HStack>

        {!isNew && employee && (
          <>
            <DismissModal
              isModalOpen={isOpenDismissModal}
              employeeId={employee.id}
              onOk={handleDismissOk}
              onCancel={() => setIsOpenDismissModal(false)}
              enums={enums}
            />

            <TransferModal
              isModalOpen={isOpenTransferModal}
              employeeId={employee.id}
              currentDepartmentId={employee.department_id}
              currentOfficeId={employee.office_id}
              currentRegionId={employee.region_id}
              onOk={handleTransferOk}
              onCancel={() => setIsOpenTransferModal(false)}
              enums={enums}
            />

            <ChangePositionModal
              isModalOpen={isOpenChangePositionModal}
              employeeId={employee.id}
              currentPostId={employee.post_id}
              onOk={handleChangePositionOk}
              onCancel={() => setIsOpenChangePositionModal(false)}
              enums={enums}
            />

            <RehireModal
              isModalOpen={isOpenRehireModal}
              employeeId={employee.id}
              onOk={handleRehireOk}
              onCancel={() => setIsOpenRehireModal(false)}
              enums={enums}
            />
          </>
        )}
      </>
    )
  },
)

EmployeeFormHeader.displayName = 'EmployeeFormHeader'
